/** Estado comercial de tarjetas del Panel General (entrar vs solicitar módulo vs pendiente). */
import { entitlementIdForWorkModule, isEntitlementActive } from "@/modules/catalogModulos";
import {
  hasActiveCommercialLicense,
  isSubmissionBlocked,
  resolveActiveModuleIds,
  resolveRequestableModuleIds,
} from "@/lib/commercialLicenseRequests";
import { WORK_MODULES } from "@/modules/workModuleCore";

export const HUB_MODULE_ACTION = {
  ENTER: "enter",
  REQUEST_MODULE: "request_module",
  PENDING: "pending",
};

/**
 * ¿La solicitud pendiente en gate incluye este módulo comercial?
 * Si gate no trae requested_modules, se trata como bloqueo global.
 */
export function gatePendingIncludesModule(gate, commercialModuleId) {
  if (!gate?.blocked || !commercialModuleId) return false;
  const mods = gate.requested_modules;
  if (!mods || typeof mods !== "object") return true;
  return Boolean(mods[commercialModuleId]);
}

/**
 * ¿La licencia activa (system/info) ya incluye este módulo comercial?
 * Fuente de verdad comercial — evita abrir «Solicitud comercial» por un falso
 * negativo de capabilities / entitlements en UI.
 */
export function isCommercialModuleAuthorizedInLicense(systemInfo, commercialModuleId) {
  if (!systemInfo || !commercialModuleId) return false;
  if (!hasActiveCommercialLicense(systemInfo)) return false;
  const activeIds = resolveActiveModuleIds(systemInfo);
  if (activeIds.includes(commercialModuleId)) return true;
  // Fallback por flags / entitlements embebidos
  const perms = systemInfo.module_permissions || {};
  return isEntitlementActive(commercialModuleId, {
    entitlements: systemInfo.entitlements || perms.entitlements,
    modulePermissions: perms,
  });
}

/**
 * @param {string} cardId — WORK_MODULES.*
 * @param {{ hasModuleAccess?: boolean|null, systemInfo?: object, gate?: object }} ctx
 */
export function resolveHubModuleCommercialAction(
  cardId,
  { hasModuleAccess = false, systemInfo = null, gate = null } = {},
) {
  if (cardId === WORK_MODULES.DISENADOR) {
    return { action: HUB_MODULE_ACTION.ENTER, label: "Entrar al módulo", disabled: false };
  }

  const commercialModuleId = entitlementIdForWorkModule(cardId);
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  const licensedOk = isCommercialModuleAuthorizedInLicense(systemInfo, commercialModuleId);

  // Licencia ya autoriza el módulo → entrar (aunque capabilities diga false).
  if (hasModuleAccess === true || licensedOk) {
    return { action: HUB_MODULE_ACTION.ENTER, label: "Entrar al módulo", disabled: false };
  }
  if (hasModuleAccess == null) {
    return {
      action: HUB_MODULE_ACTION.ENTER,
      label: "Cargando acceso…",
      disabled: true,
      commercialModuleId,
      accessLoading: true,
    };
  }

  if (
    hasActiveLicense
    && gate
    && isSubmissionBlocked(gate)
    && gatePendingIncludesModule(gate, commercialModuleId)
  ) {
    return {
      action: HUB_MODULE_ACTION.PENDING,
      label: "Solicitud pendiente",
      disabled: true,
      commercialModuleId,
    };
  }

  // Solo pedir módulo si realmente falta en la licencia (hay algo requestable).
  if (
    hasActiveLicense
    && commercialModuleId
    && resolveRequestableModuleIds(systemInfo).includes(commercialModuleId)
  ) {
    return {
      action: HUB_MODULE_ACTION.REQUEST_MODULE,
      label: "Solicitar módulo",
      disabled: false,
      commercialModuleId,
    };
  }

  return { action: HUB_MODULE_ACTION.ENTER, label: "Entrar al módulo", disabled: false };
}

/** @param {object} [systemInfo] */
export function allMainModulesActive(systemInfo = {}) {
  return resolveRequestableModuleIds(systemInfo).length === 0
    && hasActiveCommercialLicense(systemInfo);
}

/**
 * ¿Abrir modal de ampliación por deep link de «Agregar módulos»?
 * Si la licencia ya tiene todos los módulos (o el solicitado), no abrir.
 */
export function shouldOpenModuleExpansionPrefill(systemInfo, prefill) {
  if (!prefill || typeof prefill !== "object") return false;
  const kind = String(prefill.expansion_type || "").trim();
  const mods = prefill.requested_modules && typeof prefill.requested_modules === "object"
    ? prefill.requested_modules
    : {};
  const requestedIds = Object.keys(mods).filter((id) => mods[id]);
  const isModuleKind = kind === "module_upgrade" || kind === "module" || requestedIds.length > 0;
  if (!isModuleKind && kind) {
    // Renovación / usuarios / clientes: sí abrir.
    return true;
  }
  if (!isModuleKind) return Boolean(kind || requestedIds.length);
  if (!hasActiveCommercialLicense(systemInfo)) return true;
  const requestable = new Set(resolveRequestableModuleIds(systemInfo));
  if (requestedIds.length) {
    return requestedIds.some((id) => requestable.has(id));
  }
  return requestable.size > 0;
}
